import { RefObject, useLayoutEffect, useRef, useState } from "react";

const overflowChecksByElement = new Map<Element, () => void>();
let sharedResizeObserver: ResizeObserver | null = null;

const getSharedResizeObserver = () => {
  if (!sharedResizeObserver && "ResizeObserver" in window) {
    sharedResizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => overflowChecksByElement.get(entry.target)?.());
    });
  }

  return sharedResizeObserver;
};

export const useIsXOverflow = (
  ref: RefObject<any>,
  callback?: (hasOverflow: boolean) => any
) => {
  const [isOverflow, setIsOverflow] = useState(false);

  // Keep the latest callback without re-subscribing the observer when it changes
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element) return undefined;

    const checkOverflow = () => {
      const hasOverflow = element.scrollWidth > element.clientWidth;

      setIsOverflow(hasOverflow);
      callbackRef.current?.(hasOverflow);
    };

    const resizeObserver = getSharedResizeObserver();

    overflowChecksByElement.set(element, checkOverflow);
    resizeObserver?.observe(element);

    checkOverflow();

    // The old version never disconnected its observer: with virtualization,
    // rows mount/unmount constantly, so observers (and their setState calls
    // on unmounted components) would pile up.
    return () => {
      overflowChecksByElement.delete(element);
      resizeObserver?.unobserve(element);
    };
  }, [ref]);

  return isOverflow;
};
